import { useQuery } from "@tanstack/react-query";
import { xano, type ComplianceDeadline } from "@/lib/xano";
import { useCompany } from "@/lib/company";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarClock } from "lucide-react";
import { dateUk } from "@/lib/format";

const urgencyVariant = (u: string): "destructive" | "warning" | "secondary" => {
  if (u === "urgent") return "destructive";
  if (u === "soon") return "warning";
  return "secondary";
};

const typeLabel: Record<string, string> = {
  companies_house: "Companies House",
  vat: "HMRC VAT",
  ioss: "IOSS",
};

export function ComplianceCalendar() {
  const { activeToken } = useCompany();
  const q = useQuery({
    queryKey: ["compliance", activeToken],
    queryFn: () => xano.compliance.calendar(activeToken!),
    enabled: !!activeToken,
  });

  const deadlines = (q.data?.deadlines ?? []) as ComplianceDeadline[];

  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold">Compliance calendar</h2>
        </div>
        {q.isLoading ? (
          <div className="animate-pulse text-sm text-muted-foreground">Loading deadlines…</div>
        ) : deadlines.length === 0 ? (
          <p className="text-sm text-muted-foreground">No upcoming deadlines detected.</p>
        ) : (
          <ul className="space-y-2.5">
            {deadlines.map((d, i) => (
              <li key={i} className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{d.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {typeLabel[d.type] ?? d.type} · {dateUk(d.due_date)}
                  </div>
                </div>
                <Badge variant={urgencyVariant(d.urgency)}>
                  {d.days_until < 0 ? "overdue" : `${d.days_until}d`}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
